import * as React from "react";
import { EnrichedSuggestion, ToolGateCorrection, BetterAnswer, type BlockReceipt } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Post-process signal blocks - the post-answer affordance batch the runtime's
 * response post-processor emits (ais-agent-runtime#115 PostProcessSignals).
 * The family's first member, MemorySave, shipped in 1.8.0 (#194) and has its
 * own demo; this section covers its three siblings. enriched-suggestion /
 * better-answer are INTERACTIVE (settle on action via the shared receipt
 * convention); tool-gate-correction is a display-only inline footnote.
 */
export function SignalBlocksDemo() {
  const [suggestionReceipt, setSuggestionReceipt] = React.useState<BlockReceipt | undefined>(
    undefined,
  );
  const [betterReceipt, setBetterReceipt] = React.useState<BlockReceipt | undefined>(undefined);

  return (
    <Section
      id="signal-blocks"
      title="Post-process signal blocks"
      description="Post-answer affordances the runtime's response post-processor emits (ais-agent-runtime#115), completing the family MemorySave (v1.8.0) started. The platform's BlockRenderer renders each as a typed block; the interactive ones settle on action via the shared receipt convention."
    >
      <VariantBlock
        label="enriched-suggestion: kind + confidence chips"
        caption="click a chip to settle"
      >
        <div className="w-full max-w-xl">
          <EnrichedSuggestion
            prompt="Want to go deeper?"
            suggestions={[
              { label: "Re-ask this as a quiz", kind: "experience", confidence: 0.82 },
              { label: "Rewrite with sources", kind: "rewrite", confidence: 0.55 },
              { label: "What did you skip?", kind: "question", confidence: 0.3 },
              { label: "Explore related studios", kind: "explore" },
            ]}
            receipt={suggestionReceipt}
            onSelect={(s) =>
              setSuggestionReceipt({
                outcome: "success",
                summary: `You asked: "${s.value ?? s.label}"`,
                at: new Date().toISOString(),
              })
            }
          />
          {suggestionReceipt ? (
            <button
              type="button"
              className="mt-3 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => setSuggestionReceipt(undefined)}
            >
              Reset
            </button>
          ) : null}
        </div>
      </VariantBlock>

      <VariantBlock label="tool-gate-correction (display-only)">
        <div className="w-full max-w-xl">
          <ToolGateCorrection message="To actually publish this, use the **Publish** control above. I can only draft it in chat." />
        </div>
      </VariantBlock>

      <VariantBlock label="better-answer: the shared 'answer this better' nudge">
        <div className="w-full max-w-xl">
          <BetterAnswer
            kind="rewrite"
            label="I think I can answer this better with sources. Want me to try?"
            rewrittenQuery="Re-answer the previous question, citing sources from the content vault."
            receipt={betterReceipt}
            onAccept={() =>
              setBetterReceipt({
                outcome: "success",
                summary: "Re-answering with sources",
                at: new Date().toISOString(),
              })
            }
            onDismiss={() =>
              setBetterReceipt({
                outcome: "cancelled",
                summary: "Kept the original answer",
                at: new Date().toISOString(),
              })
            }
          />
          {betterReceipt ? (
            <button
              type="button"
              className="mt-3 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => setBetterReceipt(undefined)}
            >
              Reset
            </button>
          ) : null}
        </div>
      </VariantBlock>

      <VariantBlock label="better-answer kinds (question / experience / rewrite)">
        <div className="flex w-full max-w-xl flex-col gap-3">
          <BetterAnswer
            kind="question"
            label="Want me to re-ask this as a proper multiple-choice question?"
            rewrittenQuery="Re-ask the previous question as a structured choice."
          />
          <BetterAnswer
            kind="experience"
            label="This sounds like a great quiz. Should I create one?"
            rewrittenQuery="Create a quiz from the topic we just discussed."
          />
          <BetterAnswer
            kind="rewrite"
            label="I can ground this more. Re-run with a sharper search?"
            rewrittenQuery="Re-answer with a focused retrieval over the vault."
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Pre-settled (history reload)">
        <div className="flex w-full max-w-xl flex-col gap-3">
          <EnrichedSuggestion
            suggestions={[{ label: "Create a quiz", kind: "experience" }]}
            receipt={{
              outcome: "success",
              summary: 'You asked: "Create a quiz"',
              at: "2026-06-20T09:15:00Z",
            }}
          />
          <BetterAnswer
            kind="rewrite"
            label="I can answer this better"
            rewrittenQuery="q"
            receipt={{
              outcome: "success",
              summary: "Re-answered with sources",
              at: "2026-06-20T09:16:00Z",
            }}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
